import React from 'react';
import { Card, CardContent, Typography, Divider, Grid, Box, Paper, Chip } from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const PaymentInfo = ({ pagos, error }) => {
  // No mostrar nada si hay error o si no hay datos de pagos
  if (error || !pagos) return null;

  const getStatusColor = (estado) => {
    switch(estado) {
      case 'Al día':
        return 'success';
      case 'Con deuda vencida':
        return 'error';
      case 'Pendiente de pago':
        return 'warning';
      default:
        return 'default';
    }
  };

  const hasDebt = pagos.cuotas_adeudadas > 0;
  const isOverdue = pagos.estado === 'Con deuda vencida';

  return (
    <Card sx={{ mb: 3, boxShadow: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <MonetizationOnIcon sx={{ mr: 1 }} />
            Estado de Pagos
          </Typography>
          <Chip 
            label={pagos.estado || 'Sin información'} 
            color={getStatusColor(pagos.estado)}
            variant="outlined"
            size="small"
          />
        </Box>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={3}>
          {/* Cuotas */}
          <Grid item xs={12} md={4}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                height: '100%',
                borderLeft: `4px solid ${hasDebt ? (isOverdue ? '#f44336' : '#ff9800') : '#4caf50'}`,
                bgcolor: hasDebt ? (isOverdue ? '#ffebee' : '#fff8e1') : '#e8f5e9'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {hasDebt ? (
                  <WarningAmberIcon color={isOverdue ? 'error' : 'warning'} sx={{ mr: 1 }} />
                ) : (
                  <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} />
                )}
                <Typography variant="subtitle1">
                  {hasDebt ? 'Cuotas Pendientes' : 'Al día'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h4" color={hasDebt ? (isOverdue ? 'error' : 'warning.dark') : 'success.main'}>
                  {pagos.cuotas_adeudadas || 0}
                </Typography>
                {pagos.cuotas_totales > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    / {pagos.cuotas_totales} totales
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {hasDebt 
                  ? isOverdue ? 'cuotas vencidas' : 'cuotas por vencer' 
                  : 'sin deudas pendientes'}
              </Typography>
            </Paper>
          </Grid>
          
          {/* Último Pago */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EventAvailableIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Último Pago</Typography>
              </Box>
              {pagos.fecha_ultimo_pago && pagos.fecha_ultimo_pago !== 'Nunca' ? (
                <>
                  <Typography variant="h5">
                    {pagos.fecha_ultimo_pago}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Último pago registrado
                  </Typography>
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Sin registros de pago
                </Typography>
              )}
            </Paper>
          </Grid>
          
          {/* Próximo Vencimiento */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ 
              p: 2, 
              height: '100%',
              bgcolor: isOverdue ? '#ffebee' : 'grey.50',
              borderLeft: `4px solid ${isOverdue ? '#f44336' : 'rgba(0, 0, 0, 0.12)'}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EventBusyIcon color={isOverdue ? 'error' : 'action'} sx={{ mr: 1 }} />
                <Typography variant="subtitle1">
                  {isOverdue ? 'Vencimiento' : 'Próximo Vencimiento'}
                </Typography>
              </Box>
              {pagos.proximo_vencimiento && pagos.proximo_vencimiento !== 'No especificado' ? (
                <>
                  <Typography variant="h5" color={isOverdue ? 'error' : 'text.primary'}>
                    {pagos.proximo_vencimiento}
                  </Typography>
                  <Typography variant="caption" color={isOverdue ? 'error' : 'text.secondary'}>
                    {isOverdue ? 'Vencido' : 'Fecha de vencimiento'}
                  </Typography>
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No especificado
                </Typography>
              )}
            </Paper>
          </Grid>
          
          {/* Mensaje de estado */}
          {hasDebt && (
            <Grid item xs={12}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  bgcolor: isOverdue ? '#ffebee' : '#fff8e1',
                  borderLeft: `4px solid ${isOverdue ? '#f44336' : '#ff9800'}`
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningAmberIcon color={isOverdue ? 'error' : 'warning'} />
                  <Typography variant="body2" color={isOverdue ? 'error' : 'warning.dark'}>
                    {isOverdue 
                      ? '⚠️ El afiliado tiene cuotas vencidas. Se recomienda regularizar la situación de inmediato.'
                      : 'ℹ️ El afiliado tiene cuotas pendientes de pago. Por favor, regularice su situación.'}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PaymentInfo;
